<template>
  <div class="ps-page iabf-dancer-theme">
    <router-link to="/" class="back-link">&larr; Back to Dashboard</router-link>
    <div v-if="previewOnly" class="preview-banner">
      Hidden from dancers — only you can see this schedule.
    </div>
    <h1>Photoshoots</h1>
    <p class="lede">
      Fifteen minutes, two edited photos — ${{ priceLabel }} a slot.
      <template v-if="giftSlots">Your returning-dancer gift covers {{ giftSlots }} free slot{{ giftSlots === 1 ? '' : 's' }}.</template>
    </p>

    <section v-if="isFacultyEditor && editorQueue.length" class="ps-mine">
      <h2>Photos to edit</h2>
      <p class="muted">Assigned to you. Upload the finished files when they’re ready.</p>
      <article v-for="b in editorQueue" :key="'ed-' + b.id" class="ps-booking">
        <strong>{{ b.dancerName }}</strong>
        <span class="muted"> · {{ (b.selectedPhotoIds || []).length }} picks</span>
        <div class="proof-grid">
          <div v-for="p in selectedPhotosFor(b)" :key="p.id" class="proof-card">
            <img :src="p.editedUrl || p.downloadUrl" :alt="p.fileName">
            <label class="text-btn">
              {{ p.editedUrl ? 'Replace edited' : 'Upload edited' }}
              <input type="file" accept="image/*" hidden @change="onFacultyEdited(p, b, $event)">
            </label>
          </div>
        </div>
      </article>
    </section>

    <section v-if="myBookings.length" class="ps-mine">
      <h2>Your times</h2>
      <article v-for="b in myBookings" :key="b.id" class="ps-booking">
        <div v-for="slot in b.slots" :key="slot.id">
          <strong>{{ dayLabel(slot.date) }}</strong>
          · {{ formatRange(slot) }}
          · {{ settingLabel(slot.setting) }}
          <span class="muted"> at {{ locationFor(slot.setting) }}</span>
        </div>
        <p v-if="b.giftsApplied" class="muted">{{ b.giftsApplied }} gift slot{{ b.giftsApplied === 1 ? '' : 's' }} applied.</p>
        <p v-if="b.editedReady" class="ready-note">Your edited photos are ready — scroll down to download.</p>
        <p v-else-if="b.proofsSubmitted" class="muted">
          You’re #{{ b.queuePosition || '—' }} in the editing queue.
        </p>
        <p v-else-if="b.julieChoose" class="muted">
          Julie’s picking your photos. You’ll see them here when they’re edited.
        </p>
        <div class="ps-booking-actions">
          <router-link v-if="canCancel" class="text-btn" :to="{ path: '/photoshoot' }">Change time</router-link>
          <button v-if="canCancel" type="button" class="text-btn" @click="cancelBooking(b, false)">Cancel</button>
          <button v-else type="button" class="text-btn" @click="cancelBooking(b, true)">I can’t make it</button>
        </div>
      </article>
    </section>

    <section v-if="proofBookings.length" class="ps-mine">
      <h2>Your proofs</h2>
      <article v-for="b in proofBookings" :key="'pr-' + b.id" class="ps-booking">
        <p class="lede" v-if="b.julieChoose && !b.proofsSubmitted">
          I’m on it. I’ll pick the {{ includedFor(b) }} edited photo{{ includedFor(b) === 1 ? '' : 's' }} that come with your shoot.
        </p>
        <p class="lede" v-else-if="!b.proofsSubmitted && proofStep === 'rate'">
          Love it, maybe, or no. Then we’ll look at the keepers together.
        </p>
        <p class="lede" v-else-if="!b.proofsSubmitted">
          Here are your Loves and Maybes. Love the ones you want us to edit.
        </p>
        <p class="muted">{{ includedFor(b) }} edited photo{{ includedFor(b) === 1 ? '' : 's' }} come with this shoot.</p>

        <template v-if="b.julieChoose && !b.proofsSubmitted">
          <p class="muted">You don’t need to pick anything.</p>
        </template>

        <template v-else-if="!b.proofsSubmitted && proofStep === 'rate'">
          <p v-if="!currentRatePhoto(b)" class="muted">No proofs to look at yet.</p>
          <p v-else class="rate-count">{{ rateIndex + 1 }} of {{ rateableProofs(b).length }}</p>
          <div v-if="currentRatePhoto(b)" class="rate-stage">
            <button type="button" class="proof-img-btn rate-img" @click="openProof(currentRatePhoto(b))">
              <span class="proof-watermark" aria-hidden="true">PROOF</span>
              <img :src="currentRatePhoto(b).downloadUrl" :alt="currentRatePhoto(b).fileName">
            </button>
            <div class="rate-btns">
              <button
                type="button"
                class="rate-btn love"
                :class="{ on: currentRatePhoto(b).rating === 'love' }"
                :disabled="ratingBusy"
                @click="ratePhoto(currentRatePhoto(b), 'love', b)"
              >Love</button>
              <button
                type="button"
                class="rate-btn maybe"
                :class="{ on: currentRatePhoto(b).rating === 'maybe' }"
                :disabled="ratingBusy"
                @click="ratePhoto(currentRatePhoto(b), 'maybe', b)"
              >Maybe</button>
              <button
                type="button"
                class="rate-btn no"
                :class="{ on: currentRatePhoto(b).rating === 'no' }"
                :disabled="ratingBusy"
                @click="ratePhoto(currentRatePhoto(b), 'no', b)"
              >No</button>
            </div>
            <div class="rate-nav">
              <button type="button" class="text-btn" :disabled="rateIndex === 0" @click="rateIndex -= 1">Back</button>
              <button
                type="button"
                class="text-btn"
                :disabled="rateIndex >= rateableProofs(b).length - 1"
                @click="rateIndex += 1"
              >Skip</button>
            </div>
          </div>
          <p class="muted tally">{{ loveCount(b) }} Love · {{ maybeCount(b) }} Maybe · {{ noCount(b) }} No</p>
          <button
            v-if="loveCount(b) + maybeCount(b) > 0"
            type="button"
            class="text-btn"
            @click="proofStep = 'review'"
          >Review Loves &amp; Maybes</button>
          <p class="julie-offer">
            Can’t decide?
            <button type="button" class="text-btn" :disabled="submitting" @click="askJulieChoose(b)">
              Have Julie choose for me — ${{ julieChooseLabel }}
            </button>
          </p>
        </template>

        <template v-else-if="!b.proofsSubmitted && proofStep === 'review'">
          <button type="button" class="text-btn" @click="backToRate(b)">Look at every proof again</button>
          <div v-if="keeperProofs(b).length" class="proof-grid">
            <div
              v-for="p in keeperProofs(b)"
              :key="p.id"
              class="proof-card"
              :class="{ 'is-love': p.rating === 'love', 'is-maybe': p.rating === 'maybe' }"
            >
              <button type="button" class="proof-img-btn" @click="openProof(p)">
                <span class="proof-watermark" aria-hidden="true">PROOF</span>
                <img :src="p.downloadUrl" :alt="p.fileName">
              </button>
              <div class="rate-btns compact">
                <button type="button" class="rate-btn love" :class="{ on: p.rating === 'love' }" :disabled="ratingBusy" @click="ratePhoto(p, 'love', b, true)">Love</button>
                <button type="button" class="rate-btn maybe" :class="{ on: p.rating === 'maybe' }" :disabled="ratingBusy" @click="ratePhoto(p, 'maybe', b, true)">Maybe</button>
                <button type="button" class="rate-btn no" :class="{ on: p.rating === 'no' }" :disabled="ratingBusy" @click="ratePhoto(p, 'no', b, true)">No</button>
              </div>
            </div>
          </div>
          <p v-else class="muted">Nothing in the keeper pile yet — Love or Maybe a few first.</p>
          <details v-if="noProofs(b).length" class="hidden-fold">
            <summary>No ({{ noProofs(b).length }}) — change your mind?</summary>
            <div class="proof-grid">
              <div v-for="p in noProofs(b)" :key="p.id" class="proof-card is-hid">
                <img :src="p.downloadUrl" :alt="p.fileName">
                <div class="rate-btns compact">
                  <button type="button" class="rate-btn love" :disabled="ratingBusy" @click="ratePhoto(p, 'love', b, true)">Love</button>
                  <button type="button" class="rate-btn maybe" :disabled="ratingBusy" @click="ratePhoto(p, 'maybe', b, true)">Maybe</button>
                </div>
              </div>
            </div>
          </details>
          <p class="hint">Maybes stay in the maybe pile — they won’t be edited unless you Love them.</p>
          <div class="ps-cart static">
            <p>
              {{ loveCount(b) }} Love{{ loveCount(b) === 1 ? '' : 's' }} to edit.
              <strong>{{ extraLabelFor(b) }}</strong>
            </p>
            <label class="all-proofs">
              <input v-model="buyAllProofs" type="checkbox">
              Get every proof unedited for ${{ allProofsLabel }} instead of extra edits
            </label>
            <button
              type="button"
              class="ps-complete"
              :disabled="submitting || (!loveCount(b) && !buyAllProofs)"
              @click="submitPicks(b)"
            >Send my Loves</button>
          </div>
          <p class="julie-offer">
            Can’t decide?
            <button type="button" class="text-btn" :disabled="submitting" @click="askJulieChoose(b)">
              Have Julie choose for me — ${{ julieChooseLabel }}
            </button>
          </p>
        </template>

        <template v-else>
          <div class="proof-grid">
            <div v-for="p in selectedPhotosFor(b)" :key="p.id" class="proof-card is-love">
              <button type="button" class="proof-img-btn" @click="openProof(p)">
                <span v-if="!p.editedUrl" class="proof-watermark" aria-hidden="true">PROOF</span>
                <img :src="p.editedUrl || p.downloadUrl" :alt="p.fileName">
              </button>
              <a v-if="b.buyAllProofs || b.editedReady || p.editedUrl" class="text-btn" :href="p.editedUrl || p.downloadUrl" target="_blank" rel="noopener">Download</a>
            </div>
          </div>
        </template>
      </article>
    </section>

    <p v-if="error" class="err" role="alert">{{ error }}</p>
    <p v-if="loading" class="muted">Loading times…</p>

    <div v-for="day in days" :key="day.date" class="ps-day">
      <h2>{{ day.label }}</h2>
      <div v-for="group in day.groups" :key="group.setting" class="ps-group">
        <h3>{{ settingLabel(group.setting) }} · {{ locationFor(group.setting) }}</h3>
        <ul class="ps-slots">
          <li v-for="s in group.slots" :key="s.id">
            <span class="ps-time">{{ formatRange(s) }}</span>
            <span class="muted">{{ s.directorName }}</span>
            <span v-if="s.conflict" class="ps-conflict">{{ s.conflict }}</span>
            <button
              v-else-if="isChosen(s.id)"
              type="button"
              class="ps-choose is-on"
              @click="unchoose(s)"
            >Chosen</button>
            <button
              v-else-if="canChoose(s)"
              type="button"
              class="ps-choose"
              :disabled="claiming === s.id"
              @click="choose(s)"
            >Choose</button>
            <span v-else class="muted">{{ takenLabel(s) }}</span>
          </li>
        </ul>
      </div>
    </div>

    <div v-if="chosenIds.length" class="ps-cart">
      <p>
        {{ chosenIds.length }} slot{{ chosenIds.length === 1 ? '' : 's' }} chosen.
        Held for five minutes.
        <strong>{{ chargeLabel }}</strong>
      </p>
      <button
        type="button"
        class="ps-complete"
        :disabled="completing"
        @click="complete"
      >Complete booking</button>
    </div>

    <div v-if="lightbox" class="lightbox" @click.self="lightbox = null">
      <img :src="lightbox.downloadUrl" :alt="lightbox.fileName">
      <span class="proof-watermark big" aria-hidden="true">PROOF</span>
      <button type="button" class="text-btn" @click="lightbox = null">Close</button>
    </div>
  </div>
</template>

<script>
import { db, functions, storage } from '@/firebase'
import { IABF_DANCER_YEAR } from '@/config/iabf-event'
import { isFacultyAccount } from '@/utils/iabf-faculty'
import {
  canSeeDancerPhotoshootSchedule,
  extraEditedChargeCents,
  extraGiftEditedCountFromDancer,
  formatSlotRange,
  giftPhotoshootSlotsFromDancer,
  includedEditedCount,
  locationForSetting,
  photoshootChargeCents,
  photoshootConflictLabel,
  rehearsalWindowsForDancer,
  slotIsInTheFuture
} from '@/config/iabf-photoshoot'

export default {
  name: 'IABFPhotoshoot',
  data () {
    return {
      loading: true,
      claiming: '',
      completing: false,
      submitting: false,
      error: '',
      config: {},
      slots: [],
      bookings: [],
      photos: [],
      classes: [],
      groupPieces: [],
      chosenIds: [],
      holdUntil: 0,
      proofStep: 'rate',
      rateIndex: 0,
      ratingBusy: false,
      buyAllProofs: false,
      lightbox: null
    }
  },
  computed: {
    dancer () {
      return this.$store.getters.currentIABFDancer || {}
    },
    year () {
      return Number(this.dancer.festivalYear) || IABF_DANCER_YEAR
    },
    giftSlots () {
      return giftPhotoshootSlotsFromDancer(this.dancer)
    },
    extraGiftPhotos () {
      return extraGiftEditedCountFromDancer(this.dancer)
    },
    priceLabel () {
      return ((this.config.slotPriceCents || 8500) / 100).toFixed(0)
    },
    allProofsLabel () {
      return ((this.config.allProofsCents || 20000) / 100).toFixed(0)
    },
    julieChooseLabel () {
      return ((this.config.julieChooseCents || 3000) / 100).toFixed(0)
    },
    isFacultyEditor () {
      return isFacultyAccount(this.dancer)
    },
    previewOnly () {
      const email = this.$store.getters.user && this.$store.getters.user.email
      return canSeeDancerPhotoshootSchedule(email, false) && !this.config.dancerBookingOpen
    },
    myBookings () {
      const byId = {}
      this.slots.forEach((s) => { byId[s.id] = s })
      return (this.bookings || []).filter((b) => b.status === 'booked' && b.dancerId === this.dancer.id).map((b) => ({
        ...b,
        slots: (b.slotIds || []).map((id) => byId[id]).filter(Boolean)
      }))
    },
    proofBookings () {
      return this.myBookings.filter((b) => b.proofsReady)
    },
    editorQueue () {
      const email = String(this.dancer.email || '').toLowerCase()
      const uid = this.$store.getters.user && this.$store.getters.user.uid
      return (this.bookings || []).filter((b) => {
        if (b.status !== 'booked' || !b.proofsSubmitted || b.editedReady) {
          return false
        }
        if (uid && b.editorUid && b.editorUid === uid) {
          return true
        }
        return email && b.editorEmail && b.editorEmail === email
      })
    },
    canCancel () {
      const until = String(this.config.cancelUntilIso || '').trim()
      if (!until) {
        return true
      }
      return new Date().toISOString().slice(0, 10) <= until
    },
    rehearsalWindows () {
      return rehearsalWindowsForDancer({
        year: this.year,
        classes: this.classes,
        dancer: this.dancer,
        pieces: this.$store.getters.loadedIABFPiecesforDancer || [],
        groupPieces: this.groupPieces
      })
    },
    days () {
      const byDate = {}
      this.slots.forEach((s) => {
        if (s.kind === 'break') {
          return
        }
        if (!slotIsInTheFuture(s)) {
          return
        }
        const conflict = photoshootConflictLabel(s, this.rehearsalWindows)
        const row = { ...s, conflict }
        if (!byDate[s.date]) {
          byDate[s.date] = {}
        }
        if (!byDate[s.date][s.setting]) {
          byDate[s.date][s.setting] = []
        }
        byDate[s.date][s.setting].push(row)
      })
      return Object.keys(byDate).sort().map((date) => ({
        date,
        label: this.dayLabel(date),
        groups: Object.keys(byDate[date]).sort().map((setting) => ({
          setting,
          slots: byDate[date][setting]
        }))
      }))
    },
    chargeLabel () {
      const cents = photoshootChargeCents(this.chosenIds.length, this.giftSlots, this.config.slotPriceCents)
      if (cents === 0) {
        return 'Covered by your gift — no charge.'
      }
      return 'You’ll be charged $' + (cents / 100).toFixed(2) + ' on the card on file.'
    }
  },
  created () {
    this.$store.commit('setLayout', 'iabf-layout')
    if (this.dancer.id) {
      this.$store.dispatch('loadIABFPiecesForDancer', this.dancer.id).catch(() => {})
    }
    this.ensureAllowedThenReload()
  },
  methods: {
    async ensureAllowedThenReload () {
      try {
        const config = await this.$store.dispatch('loadPhotoshootConfig', { useAdminYear: false })
        this.config = config || {}
      } catch (e) {
        this.config = {}
      }
      const email = this.$store.getters.user && this.$store.getters.user.email
      if (!canSeeDancerPhotoshootSchedule(email, this.config.dancerBookingOpen)) {
        this.$router.replace('/')
        return
      }
      this.reload()
    },
    locationFor (setting) {
      return locationForSetting(this.config, setting)
    },
    settingLabel (setting) {
      return setting === 'outdoor' ? 'Outdoor' : 'Indoor'
    },
    formatRange (s) {
      return formatSlotRange(s.startMin, s.endMin)
    },
    dayLabel (iso) {
      const [y, m, d] = String(iso || '').split('-').map(Number)
      if (!y) {
        return iso
      }
      return new Date(y, m - 1, d).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric'
      })
    },
    isChosen (id) {
      return this.chosenIds.indexOf(id) !== -1
    },
    canChoose (s) {
      if (s.conflict) {
        return false
      }
      if (s.status === 'booked') {
        return false
      }
      if (s.status === 'held' && s.holdDancerId && s.holdDancerId !== this.dancer.id) {
        return Number(s.holdUntil) <= Date.now()
      }
      return true
    },
    takenLabel (s) {
      if (s.status === 'booked') {
        return 'Taken'
      }
      return 'Held'
    },
    photosFor (bookingId) {
      return (this.photos || []).filter((p) => p.bookingId === bookingId && p.kind !== 'edited')
    },
    rateableProofs (booking) {
      return this.photosFor(booking.id).filter((p) => !(p.hidden && p.hiddenBy === 'admin'))
    },
    currentRatePhoto (booking) {
      const list = this.rateableProofs(booking)
      if (!list.length) {
        return null
      }
      const i = Math.min(Math.max(0, this.rateIndex), list.length - 1)
      return list[i] || null
    },
    keeperProofs (booking) {
      return this.rateableProofs(booking).filter((p) => p.rating === 'love' || p.rating === 'maybe')
    },
    noProofs (booking) {
      return this.rateableProofs(booking).filter((p) => p.rating === 'no')
    },
    loveProofs (booking) {
      return this.rateableProofs(booking).filter((p) => p.rating === 'love')
    },
    loveCount (booking) {
      return this.loveProofs(booking).length
    },
    maybeCount (booking) {
      return this.rateableProofs(booking).filter((p) => p.rating === 'maybe').length
    },
    noCount (booking) {
      return this.noProofs(booking).length
    },
    selectedPhotosFor (booking) {
      const ids = booking.selectedPhotoIds || []
      const byId = {}
      this.photos.forEach((p) => { byId[p.id] = p })
      const fromIds = ids.map((id) => byId[id]).filter(Boolean)
      if (fromIds.length) {
        return fromIds
      }
      return this.loveProofs(booking)
    },
    includedFor (booking) {
      const slots = (booking.slotIds || []).length
      return includedEditedCount(slots, this.extraGiftPhotos)
    },
    extraLabelFor (booking) {
      if (this.buyAllProofs) {
        return 'All proofs unedited — $' + this.allProofsLabel + '.'
      }
      const cents = extraEditedChargeCents(
        this.loveCount(booking),
        this.includedFor(booking),
        this.config.extraPhotoCents
      )
      if (cents === 0) {
        return 'Covered by your package.'
      }
      return 'Extras: $' + (cents / 100).toFixed(2) + ' on the card on file.'
    },
    openProof (photo) {
      this.lightbox = photo
    },
    backToRate (booking) {
      const list = this.rateableProofs(booking)
      const first = list.findIndex((p) => !p.rating)
      this.rateIndex = first === -1 ? 0 : first
      this.proofStep = 'rate'
    },
    async ratePhoto (photo, rating, booking, stay) {
      if (!photo || this.ratingBusy) {
        return
      }
      this.ratingBusy = true
      this.error = ''
      const isNo = rating === 'no'
      try {
        await this.$store.dispatch('updatePhotoshootPhoto', {
          id: photo.id,
          useAdminYear: false,
          fields: {
            rating,
            hidden: isNo,
            hiddenBy: isNo ? 'dancer' : null
          }
        })
        photo.rating = rating
        photo.hidden = isNo
        photo.hiddenBy = isNo ? 'dancer' : null
        if (!stay && booking) {
          const list = this.rateableProofs(booking)
          const nextUnrated = list.findIndex((p, i) => i > this.rateIndex && !p.rating)
          if (nextUnrated !== -1) {
            this.rateIndex = nextUnrated
          } else {
            const firstUnrated = list.findIndex((p) => !p.rating)
            if (firstUnrated === -1) {
              this.proofStep = 'review'
            } else {
              this.rateIndex = firstUnrated
            }
          }
        }
      } catch (e) {
        this.error = (e && e.message) || 'Could not save that one. Try again?'
      }
      this.ratingBusy = false
    },
    async reload () {
      this.loading = true
      this.error = ''
      try {
        const cols = this.$store.getters.dancerIabf
        const [config, slots, bookings, photos] = await Promise.all([
          this.$store.dispatch('loadPhotoshootConfig', { useAdminYear: false }),
          this.$store.dispatch('loadPhotoshootSlots', { useAdminYear: false }),
          this.$store.dispatch('loadMyPhotoshootBookings'),
          this.$store.dispatch('loadMyPhotoshootPhotos')
        ])
        this.config = config || {}
        this.slots = slots || []
        this.bookings = bookings || []
        this.photos = photos || []
        this.syncProofStepFromPhotos()
        try {
          this.groupPieces = await this.$store.dispatch('loadGroupPieces', { useAdminYear: false })
        } catch (e) {
          this.groupPieces = []
        }
        if (cols && cols.classes) {
          const classSnap = await db.collection(cols.classes).get()
          this.classes = classSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
        }
        if (this.isFacultyEditor) {
          const email = String(this.dancer.email || '').toLowerCase()
          const uid = this.$store.getters.user && this.$store.getters.user.uid
          if (cols && cols.photoshootBookings) {
            let extra = []
            if (uid) {
              const snap = await db.collection(cols.photoshootBookings).where('editorUid', '==', uid).get()
              extra = extra.concat(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })))
            }
            if (email) {
              const snap = await db.collection(cols.photoshootBookings).where('editorEmail', '==', email).get()
              extra = extra.concat(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })))
            }
            extra.forEach((b) => {
              if (!this.bookings.some((x) => x.id === b.id)) {
                this.bookings.push(b)
              }
            })
          }
          if (cols && cols.photoshootPhotos) {
            const extraPhotos = []
            if (uid) {
              const snap = await db.collection(cols.photoshootPhotos).where('editorUid', '==', uid).get()
              extraPhotos.push(...snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })))
            }
            if (email) {
              const snap = await db.collection(cols.photoshootPhotos).where('editorEmail', '==', email).get()
              extraPhotos.push(...snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })))
            }
            extraPhotos.forEach((p) => {
              if (!this.photos.some((x) => x.id === p.id)) {
                this.photos.push(p)
              }
            })
          }
        }
      } catch (e) {
        this.error = (e && e.message) || 'Could not load photoshoot times.'
      }
      this.loading = false
    },
    syncProofStepFromPhotos () {
      const b = this.proofBookings[0]
      if (!b || b.proofsSubmitted) {
        return
      }
      const list = this.rateableProofs(b)
      if (!list.length) {
        return
      }
      const first = list.findIndex((p) => !p.rating)
      if (first === -1) {
        this.proofStep = 'review'
        this.rateIndex = 0
        return
      }
      this.proofStep = 'rate'
      this.rateIndex = first
    },
    async choose (slot) {
      this.claiming = slot.id
      this.error = ''
      try {
        const callable = functions.httpsCallable('claimPhotoshootSlot')
        const res = await callable({ year: this.year, slotId: slot.id })
        const holdUntil = res && res.data && res.data.holdUntil
        this.holdUntil = holdUntil || (Date.now() + 5 * 60 * 1000)
        if (this.chosenIds.indexOf(slot.id) === -1) {
          this.chosenIds = this.chosenIds.concat([slot.id])
        }
        slot.status = 'held'
        slot.holdDancerId = this.dancer.id
      } catch (e) {
        this.error = (e && e.message) || 'That time was just taken.'
        await this.reload()
      }
      this.claiming = ''
    },
    async unchoose (slot) {
      this.chosenIds = this.chosenIds.filter((id) => id !== slot.id)
      try {
        const callable = functions.httpsCallable('releasePhotoshootSlot')
        await callable({ year: this.year, slotId: slot.id })
      } catch (e) { /* hold will expire */ }
      slot.status = 'open'
    },
    async complete () {
      if (!this.chosenIds.length) {
        return
      }
      this.completing = true
      this.error = ''
      try {
        const callable = functions.httpsCallable('completePhotoshootBooking')
        await callable({ year: this.year, slotIds: this.chosenIds.slice() })
        this.chosenIds = []
        await this.reload()
      } catch (e) {
        this.error = (e && e.message) || 'Could not finish booking.'
      }
      this.completing = false
    },
    async cancelBooking (booking, noRefund) {
      const msg = noRefund
        ? 'We’ll keep the time open on our side. There’s no refund after the cancel-by date. Continue?'
        : 'Cancel this photoshoot? We’ll open the time for someone else.'
      if (!window.confirm(msg)) {
        return
      }
      try {
        const callable = functions.httpsCallable('cancelPhotoshootBooking')
        await callable({ year: this.year, bookingId: booking.id, noRefund: !!noRefund })
        await this.reload()
      } catch (e) {
        this.error = (e && e.message) || 'Could not update that booking.'
      }
    },
    async askJulieChoose (booking) {
      const n = this.includedFor(booking)
      const msg = 'I’ll pick the ' + n + ' edited photo' + (n === 1 ? '' : 's') +
        ' that come with your shoot. $' + this.julieChooseLabel + ' on the card on file. Sound good?'
      if (!window.confirm(msg)) {
        return
      }
      this.submitting = true
      this.error = ''
      try {
        const callable = functions.httpsCallable('requestPhotoshootJulieChoose')
        await callable({ year: this.year, bookingId: booking.id })
        await this.reload()
      } catch (e) {
        this.error = (e && e.message) || 'Could not set that up. Try again?'
      }
      this.submitting = false
    },
    async submitPicks (booking) {
      this.submitting = true
      this.error = ''
      try {
        const callable = functions.httpsCallable('submitPhotoshootProofs')
        await callable({
          year: this.year,
          bookingId: booking.id,
          photoIds: this.loveProofs(booking).map((p) => p.id),
          buyAllProofs: this.buyAllProofs
        })
        this.buyAllProofs = false
        await this.reload()
      } catch (e) {
        this.error = (e && e.message) || 'Could not send your picks.'
      }
      this.submitting = false
    },
    async onFacultyEdited (photo, booking, ev) {
      const file = ev && ev.target && ev.target.files && ev.target.files[0]
      if (ev && ev.target) {
        ev.target.value = ''
      }
      if (!file) {
        return
      }
      const cols = this.$store.getters.dancerIabf
      const prefix = (cols && cols.storage && cols.storage.photoshoot) || ('iabf-photoshoot-' + this.year)
      const safe = String(file.name || 'edited.jpg').replace(/[^\w.\-]+/g, '_')
      const path = prefix + '/edited/' + booking.id + '/' + Date.now() + '-' + safe
      try {
        const snap = await storage.ref(path).put(file, { contentType: file.type || 'image/jpeg' })
        const url = await snap.ref.getDownloadURL()
        await this.$store.dispatch('updatePhotoshootPhoto', {
          id: photo.id,
          useAdminYear: false,
          fields: {
            edited: true,
            editedUrl: url,
            editedStoragePath: path,
            editedAt: Date.now()
          }
        })
        photo.edited = true
        photo.editedUrl = url
      } catch (e) {
        this.error = (e && e.message) || 'Could not upload the edited file.'
      }
    }
  }
}
</script>

<style scoped>
.ps-page {
  max-width: 720px;
  margin: 0 auto;
  padding: 24px 16px 96px;
}
.back-link { color: var(--iabf-ink-faint, #8a8792); text-decoration: none; font-size: 15px; }
.preview-banner {
  margin: 12px 0 0;
  padding: 8px 12px;
  border-radius: 10px;
  background: #efe8dc;
  color: #5c5a63;
  font-size: 14px;
}
h1 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 32px;
  margin: 12px 0 8px;
}
.lede { font-size: 17px; color: var(--iabf-ink-muted, #5c5a63); line-height: 1.5; }
.muted { color: var(--iabf-ink-muted, #5c5a63); }
.err { color: var(--iabf-danger, #b54a4a); }
.ready-note { color: #2f6b4f; font-weight: 600; }
.ps-mine { margin: 24px 0; }
.ps-booking {
  background: #fff;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 14px;
  padding: 14px 16px;
  margin-bottom: 10px;
}
.ps-booking-actions { margin-top: 8px; display: flex; gap: 12px; }
.text-btn {
  border: none;
  background: none;
  color: var(--iabf-primary, #8a957d);
  font: inherit;
  font-weight: 700;
  cursor: pointer;
  text-decoration: underline;
}
.text-btn.on { color: #2f6b4f; }
.ps-day { margin: 28px 0; }
.ps-day h2, .ps-group h3 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
}
.ps-group h3 { font-size: 18px; margin: 12px 0 8px; }
.ps-slots { list-style: none; padding: 0; margin: 0; }
.ps-slots li {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid var(--iabf-border, #e3e0e8);
}
.ps-time { font-weight: 700; min-width: 140px; }
.ps-conflict { color: var(--iabf-warning, #c4923a); font-size: 14px; }
.ps-choose {
  margin-left: auto;
  border: 1.5px solid var(--iabf-primary, #8a957d);
  background: #fff;
  color: var(--iabf-primary, #8a957d);
  border-radius: 999px;
  padding: 6px 14px;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}
.ps-choose.is-on {
  background: var(--iabf-primary, #8a957d);
  color: #fff;
}
.ps-cart {
  position: sticky;
  bottom: 0;
  background: var(--iabf-bg-soft, #eef0eb);
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 16px 16px 0 0;
  padding: 14px 16px 18px;
  margin: 24px -16px 0;
}
.ps-cart.static {
  position: static;
  margin: 16px 0 0;
  border-radius: 12px;
}
.ps-complete {
  margin-top: 8px;
  border: none;
  background: var(--iabf-primary, #8a957d);
  color: #fff;
  font: inherit;
  font-weight: 700;
  border-radius: 10px;
  padding: 10px 16px;
  cursor: pointer;
}
.proof-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
  margin-top: 12px;
}
.proof-card { background: #f7f6f3; border-radius: 10px; padding: 6px; }
.proof-card.is-love { outline: 2px solid var(--iabf-primary, #8a957d); }
.proof-card.is-maybe { outline: 2px dashed #c4b8a5; }
.proof-card.is-hid { opacity: 0.55; }
.rate-count {
  font-size: 14px;
  color: var(--iabf-ink-muted, #5c5a63);
  margin: 8px 0;
}
.rate-stage { margin-top: 8px; }
.rate-img img {
  width: 100%;
  height: auto;
  max-height: 62vh;
  object-fit: contain;
  background: #1c1b1f;
  border-radius: 12px;
}
.rate-btns {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}
.rate-btns.compact { gap: 6px; }
.rate-btns.compact .rate-btn {
  min-height: 36px;
  font-size: 12px;
  padding: 4px 0;
}
.rate-btn {
  flex: 1;
  min-height: 48px;
  border-radius: 999px;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  background: #fff;
  font: inherit;
  font-weight: 700;
  cursor: pointer;
}
.rate-btn.love.on,
.rate-btn.love:hover { background: var(--iabf-primary, #8a957d); color: #fff; border-color: var(--iabf-primary, #8a957d); }
.rate-btn.maybe.on { background: #efe8dc; border-color: #c4b8a5; }
.rate-btn.no.on { background: #f3e4e4; border-color: #d4b0b0; color: #8a4a4a; }
.rate-nav {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
}
.tally { margin-top: 12px; }
.hint { font-size: 15px; color: var(--iabf-ink-muted, #5c5a63); margin: 12px 0 0; }
.julie-offer {
  margin-top: 16px;
  font-size: 15px;
  color: var(--iabf-ink-muted, #5c5a63);
}
.proof-img-btn {
  position: relative;
  display: block;
  width: 100%;
  border: none;
  padding: 0;
  background: none;
  cursor: zoom-in;
}
.proof-card img { width: 100%; height: 140px; object-fit: cover; border-radius: 8px; display: block; }
.proof-watermark {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255,255,255,0.72);
  font-weight: 800;
  letter-spacing: 0.2em;
  font-size: 18px;
  pointer-events: none;
  text-shadow: 0 1px 6px rgba(0,0,0,0.45);
}
.proof-watermark.big { font-size: 48px; }
.proof-actions { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px; }
.hidden-fold { margin-top: 12px; }
.all-proofs { display: flex; gap: 8px; align-items: flex-start; font-size: 14px; margin: 8px 0; }
.lightbox {
  position: fixed;
  inset: 0;
  background: rgba(20,18,16,0.88);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 80;
  padding: 24px;
}
.lightbox img { max-width: 96vw; max-height: 80vh; position: relative; }
.lightbox .proof-watermark { position: absolute; }
</style>
